import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useSearch } from "@redotech/react-util/search";
import {
  ReturnTagSource,
  Tag,
  TagKind,
} from "@redotech/redo-common-model-tags-common/tag";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { CreateTagModal } from "@redotech/redo-web/tags/create-tag-modal";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
  TagPill,
} from "@redotech/redo-web/tags/tag-pill";
import { unique } from "@redotech/util/array";
import Fuse from "fuse.js";
import { memo, useEffect, useId, useMemo, useState } from "react";

export interface TagSelectorProps {
  /**
   * Current tags that are selected
   */
  tags: Tag[];
  /**
   * Callback when tags are changed
   */
  onTagsChange: (tags: Tag[]) => void;
  /**
   * Whether to show pills with delete option (for existing returns) or badges (for flow configuration)
   */
  displayMode?: "pills" | "badges";
  /**
   * Whether to allow tag deletion (only for pills mode)
   */
  allowDelete?: boolean;
  /**
   * Custom handler for tag deletion
   */
  onTagDelete?: (tag: Tag) => void;
  /**
   * Whether to filter out system tags
   */
  filterSystemTags?: boolean;
  /**
   * Custom static items to show at the bottom of the dropdown
   */
  customStaticItems?: RedoListItem<void>[];
  /**
   * Custom handler for static item selection
   */
  onStaticItemSelected?: (item: RedoListItem<void>) => void;
  /**
   * Whether the component is read-only
   */
  readOnly?: boolean;
  /**
   * Hide the selected tags display - only show the + button.
   * Tags are still tracked for dropdown selection state.
   */
  hideSelectedTags?: boolean;
}

export const TagSelector = memo(function TagSelector({
  tags,
  onTagsChange,
  displayMode = "badges",
  allowDelete = false,
  onTagDelete,
  filterSystemTags = false,
  customStaticItems,
  onStaticItemSelected,
  readOnly = false,
  hideSelectedTags = false,
}: TagSelectorProps) {
  const client = useRequiredContext(RedoMerchantRpcClientContext);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);
  const [selectDropdownOpen, setSelectDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingTagName, setPendingTagName] = useState("");
  const id = useId();

  const availableTagsLoad = useLoad(
    () => client.getTeamTags({ kind: TagKind.RETURN }),
    [client],
  );

  const [availableTags, setAvailableTags] = useState<Tag[]>([]);

  useEffect(() => {
    let loadedTags = availableTagsLoad.value?.tags ?? [];
    if (filterSystemTags) {
      loadedTags = loadedTags.filter(
        (tag) => tag.source !== ReturnTagSource.REDO,
      );
    }
    setAvailableTags(loadedTags);
  }, [availableTagsLoad, filterSystemTags]);

  const tagOptions = useMemo(() => {
    const uniqueTags = unique([...tags, ...availableTags], (tag) => tag.name);

    return uniqueTags.map<RedoListItem<Tag>>((tag) => ({
      type: "badge",
      badge: {
        text: tag.name,
        color: BadgePillThemeToColorMapping[tag.pillTheme],
        type: BadgePillThemeToTypeMapping[tag.pillTheme],
      },
      id: tag.name,
      menu:
        allowDelete && onTagDelete && tag.source !== ReturnTagSource.REDO
          ? { onClick: () => onTagDelete(tag) }
          : undefined,
      value: tag,
    }));
  }, [availableTags, tags, allowDelete, onTagDelete]);

  const selectedTagOptions = useMemo(() => {
    const selectedTagNames = new Set(tags.map((tag) => tag.name));
    return tagOptions.filter((tag) => selectedTagNames.has(tag.value.name));
  }, [tags, tagOptions]);

  const tagSearcher = useMemo(() => {
    return new Fuse<RedoListItem<Tag>>([], {
      keys: ["value.name", "badge.text"],
      threshold: 0.3,
    });
  }, []);

  const searchedTags = useSearch(tagSearcher, tagOptions, search);

  const defaultStaticItems = useMemo(() => {
    const createTagItem: RedoListItem<void> = {
      id: `create-tag-${id}`,
      type: "text",
      text: "Create new tag",
      value: undefined,
      leadingItem: { type: "icon", Icon: PlusIcon },
    };
    return [createTagItem];
  }, [id]);

  const staticItems = customStaticItems || defaultStaticItems;

  const handleStaticItemSelected = useHandler((item: RedoListItem<void>) => {
    if (onStaticItemSelected) {
      onStaticItemSelected(item);
    } else if (item.id === `create-tag-${id}`) {
      setSelectDropdownOpen(false);
      setPendingTagName(search || "New Tag");
      setCreateTagModalOpen(true);
    }
  });

  const handleCreateTag = useHandler(
    async ({ name, pillTheme }: { name: string; pillTheme: PillTheme }) => {
      const newTag: Tag = {
        name,
        pillTheme,
        source: ReturnTagSource.MERCHANT,
        kind: TagKind.RETURN,
      };

      try {
        await client.upsertTag({ tag: newTag });

        // only update UI state after successfull API call
        const updatedTags = [...tags, newTag];
        onTagsChange(updatedTags);

        setAvailableTags((prev) => {
          const exists = prev.some((tag) => tag.name === newTag.name);
          return exists ? prev : [...prev, newTag];
        });

        setPendingTagName("");
        setCreateTagModalOpen(false);
      } catch (error) {
        console.error("Failed to create return tag:", error);
        // Consider adding user-facing error handling here
      }
    },
  );

  const handleTagSelection = useHandler((items: RedoListItem<Tag>[]) => {
    const newTags = items.map((item) => item.value);
    onTagsChange(newTags);
  });

  const handleCancelCreateTag = useHandler(() => {
    setPendingTagName("");
    setCreateTagModalOpen(false);
    setSelectDropdownOpen(true);
  });

  if (readOnly) {
    return (
      <Flex gap="sm" wrap="wrap">
        {tags.map((tag) =>
          displayMode === "pills" ? (
            <TagPill badgeSize="sm" key={tag.name} tag={tag} />
          ) : (
            <RedoBadge
              color={BadgePillThemeToColorMapping[tag.pillTheme]}
              key={tag.name}
              size="sm"
              text={tag.name}
              type={BadgePillThemeToTypeMapping[tag.pillTheme]}
            />
          ),
        )}
      </Flex>
    );
  }

  return (
    <>
      <Flex
        cursor="pointer"
        onClick={(e) => {
          e.stopPropagation();
          setSelectDropdownOpen(!selectDropdownOpen);
        }}
        ref={setDropdownAnchor}
        w="full"
      >
        <Flex gap="sm" wrap="wrap">
          {!hideSelectedTags &&
            tags.map((tag) =>
              displayMode === "pills" ? (
                <TagPill badgeSize="sm" key={tag.name} tag={tag} />
              ) : (
                <RedoBadge
                  color={BadgePillThemeToColorMapping[tag.pillTheme]}
                  key={tag.name}
                  size="sm"
                  text={tag.name}
                />
              ),
            )}
          <RedoBadge
            color="white"
            iconTrailing={{ type: "icon", Icon: PlusIcon }}
            size="sm"
            type="alternate"
          />
        </Flex>
      </Flex>

      <RedoMultiselectDropdown<Tag>
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={selectDropdownOpen}
        items={searchedTags}
        searchPlaceholder="Search tags"
        searchString={search}
        selectedItems={selectedTagOptions}
        setDropdownOpen={setSelectDropdownOpen}
        setSearchString={setSearch}
        setSelectedItems={handleTagSelection}
        staticItems={staticItems}
        staticItemSelected={handleStaticItemSelected}
      />

      {(createTagModalOpen || pendingTagName) && (
        <CreateTagModal
          cancelClicked={handleCancelCreateTag}
          handleCreateTag={handleCreateTag}
        />
      )}
    </>
  );
});
